/**
 * Patient-side card for requesting a one-time 3-day delivery window for an
 * upcoming order. Lives on the Patient Dashboard AND on the next-order detail
 * page so patients can request it either contextually or proactively.
 *
 * Rules enforced inline so patients don't waste time submitting an invalid
 * window:
 *   - window start must be ≥ today + 4 days (pharmacy needs 3-day lead)
 *   - window end must be ≥ start and ≤ start + 2 days (3 days max span)
 *   - reason ≥ 5 chars
 *   - only ONE active request at a time — surfaced with chat/phone fallback
 *
 * Status badges: Pending (amber) / Approved (emerald) / Denied (slate).
 */
import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar, Plane, AlertCircle, CheckCircle2, Clock, XCircle, Phone, MessageSquare } from "lucide-react";
import { toast } from "sonner";

function isoDate(d) {
  return d.toISOString().slice(0, 10);
}

function addDays(dateStr, n) {
  const d = new Date(dateStr + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return isoDate(d);
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso + "T12:00:00Z").toLocaleDateString(undefined, {
      weekday: "short", month: "short", day: "numeric", year: "numeric",
    });
  } catch { return iso; }
}

const STATUS_META = {
  pending: { label: "Pending review", Icon: Clock, color: "bg-amber-100 text-amber-700 border-amber-300" },
  approved: { label: "Approved", Icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  denied: { label: "Not approved", Icon: XCircle, color: "bg-slate-100 text-slate-700 border-slate-300" },
};

export default function DeliveryExceptionCard({ compact = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const today = new Date();
  const minStart = addDays(isoDate(today), 4);
  const [windowStart, setWindowStart] = useState(minStart);
  const [windowEnd, setWindowEnd] = useState(addDays(minStart, 2));
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/delivery-exceptions/mine");
      setItems(data || []);
    } catch (e) {
      console.error("delivery exceptions load failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Active = pending OR approved-but-unconsumed. Blocks new submissions.
  const active = items.find(
    (it) => it.status === "pending" || (it.status === "approved" && !it.consumed_order_id),
  );

  const onStartChange = (val) => {
    setWindowStart(val);
    // Auto-adjust end to stay within 3-day max span.
    const maxEnd = addDays(val, 2);
    if (windowEnd < val) setWindowEnd(val);
    else if (windowEnd > maxEnd) setWindowEnd(maxEnd);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 5) {
      toast.error("Please share a brief reason (5 char min)");
      return;
    }
    setSubmitting(true);
    try {
      await API.post("/delivery-exceptions", {
        window_start: windowStart,
        window_end: windowEnd,
        reason: reason.trim(),
      });
      toast.success("Request submitted — we'll email you once a team member reviews it");
      setShowForm(false);
      setReason("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit request");
    } finally {
      setSubmitting(false);
    }
  };

  const span = (() => {
    try {
      const a = new Date(windowStart + "T00:00:00Z");
      const b = new Date(windowEnd + "T00:00:00Z");
      return Math.round((b - a) / (1000 * 60 * 60 * 24)) + 1;
    } catch { return 1; }
  })();

  return (
    <Card className="border-purple-200 dark:border-purple-900/40" data-testid="delivery-exception-card">
      <CardContent className={compact ? "p-4" : "p-5"}>
        <div className="flex items-start gap-3 mb-3">
          <span className="w-9 h-9 rounded-lg bg-gradient-to-br from-purple-100 to-violet-100 flex items-center justify-center text-purple-600 flex-shrink-0">
            <Plane className="w-4 h-4" />
          </span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-slate-900 dark:text-white text-base" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Traveling? Request a delivery window
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed mt-0.5">
              Tell us a 3-day window for your next shipment and we&apos;ll work with the pharmacy to time it.
              One request at a time — additional changes need a quick chat or call.
            </p>
          </div>
        </div>

        {/* Active request banner */}
        {active && !showForm && (
          <ActiveBanner item={active} />
        )}

        {/* History (compact list when not actively requesting) */}
        {!showForm && items.length > 0 && (
          <div className="space-y-1.5 mt-3">
            {items.slice(0, compact ? 2 : 5).map((it) => (
              <HistoryRow key={it.id} item={it} />
            ))}
          </div>
        )}

        {/* Empty state when not loading and no items */}
        {!loading && items.length === 0 && !showForm && (
          <p className="text-xs text-slate-400 italic mt-2">
            No delivery window requests yet.
          </p>
        )}

        {/* New request form (only when no active request) */}
        {!active && showForm && (
          <form onSubmit={submit} className="mt-4 space-y-3" data-testid="delivery-exception-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                  Window starts
                </label>
                <input
                  type="date"
                  value={windowStart}
                  min={minStart}
                  onChange={(e) => onStartChange(e.target.value)}
                  className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500/30"
                  required
                  data-testid="delivery-exception-start"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                  Window ends
                </label>
                <input
                  type="date"
                  value={windowEnd}
                  min={windowStart}
                  max={addDays(windowStart, 2)}
                  onChange={(e) => setWindowEnd(e.target.value)}
                  className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500/30"
                  required
                  data-testid="delivery-exception-end"
                />
              </div>
            </div>
            <p className="text-[10px] text-slate-400">
              Max 3-day span. We need at least 4 days lead time so the pharmacy can plan the shipment.
              <span className="text-purple-600 font-semibold"> {span} day{span > 1 ? "s" : ""} selected.</span>
            </p>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                Why? (helps us approve faster)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="e.g. Traveling out of town May 10–13; want delivery before I leave."
                className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500/30"
                required
                data-testid="delivery-exception-reason"
              />
              <p className="text-[10px] text-slate-400 text-right">{reason.length}/500</p>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Button
                type="submit"
                disabled={submitting}
                className="rounded-full bg-purple-600 hover:bg-purple-700 text-white text-sm"
                data-testid="delivery-exception-submit"
              >
                {submitting ? "Submitting…" : "Submit request"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowForm(false)}
                disabled={submitting}
                className="text-sm"
              >Cancel</Button>
            </div>
          </form>
        )}

        {/* New-request CTA */}
        {!active && !showForm && (
          <Button
            type="button"
            onClick={() => setShowForm(true)}
            className="rounded-full bg-purple-600 hover:bg-purple-700 text-white text-sm mt-3"
            data-testid="delivery-exception-new-btn"
          >
            <Calendar className="w-3.5 h-3.5 mr-1.5" /> Request a delivery window
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function ActiveBanner({ item }) {
  const meta = STATUS_META[item.status] || STATUS_META.pending;
  const { Icon } = meta;
  return (
    <div className={`rounded-lg border ${meta.color} px-3 py-2.5`} data-testid={`delivery-exception-active-${item.status}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4" />
        <span className="text-xs font-semibold uppercase tracking-wider">{meta.label}</span>
        <span className="text-[11px] ml-auto opacity-75">
          {fmtDate(item.window_start)} → {fmtDate(item.window_end)}
        </span>
      </div>
      {item.staff_note && (
        <p className="text-xs italic mt-1 leading-relaxed opacity-80">&quot;{item.staff_note}&quot;</p>
      )}
      {item.status === "pending" && (
        <p className="text-[11px] mt-1 opacity-75">A team member will review this shortly and email you with the decision.</p>
      )}
      {item.status === "approved" && (
        <p className="text-[11px] mt-1 opacity-75">Your next order will be timed for this window. A note has been attached for the pharmacy.</p>
      )}
      <p className="text-[10px] mt-2 opacity-60 flex items-center gap-2">
        Need changes?
        <span className="inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" />Chat</span>
        <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />(727) 516-2142</span>
      </p>
    </div>
  );
}

function HistoryRow({ item }) {
  const meta = STATUS_META[item.status] || STATUS_META.pending;
  const { Icon } = meta;
  return (
    <div className="flex items-center gap-2 text-xs bg-slate-50 dark:bg-slate-900 rounded-md px-2.5 py-1.5">
      <Icon className={`w-3.5 h-3.5 ${item.status === "approved" ? "text-emerald-600" : item.status === "denied" ? "text-slate-400" : "text-amber-600"}`} />
      <span className="text-slate-700 dark:text-slate-300">
        {fmtDate(item.window_start)} → {fmtDate(item.window_end)}
      </span>
      <span className="text-slate-400 ml-auto">
        {item.consumed_at ? "Used" : meta.label}
      </span>
    </div>
  );
}
